import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { analyticsApi, formsApi } from '../lib/api';
import TrafficLineChart from '../components/TrafficLineChart';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart3, Eye, FileText, Send, TrendingUp, TrendingDown,
  ArrowLeft, Calendar, Users, Clock, ExternalLink, Loader2, ShieldOff,
  Globe, Monitor, Smartphone, MapPin, Compass, Flame, RotateCcw
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import FFLoader from '../components/FFLoader';
import FormHeatmap from '../components/FormHeatmap';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#FE6A6B';

const BreakdownPanel = ({ title, icon: Icon, data }) => {
  const sorted = data ? Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6) : [];
  const total = sorted.reduce((a, b) => a + b[1], 0);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: ACCENT }} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {sorted.length === 0 ? (
          <p className="text-xs text-zinc-400 py-2">No data yet</p>
        ) : (
          <div className="space-y-2.5">
            {sorted.map(([name, count]) => {
              const pct = total > 0 ? (count / total * 100) : 0;
              return (
                <div key={name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-zinc-700 truncate mr-2">{name}</span>
                    <span className="text-zinc-400 text-xs whitespace-nowrap">{count} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: ACCENT }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BLOCK_OWN_KEY = 'ff_block_own_activity';

/**
 * Per-element funnel — keeps the tapered visual (the right chart type
 * for "where do people drop off?") while adopting TrafficLineChart's
 * metric-chip + tooltip conventions so the whole Analytics page reads
 * as one chart family. Two metrics:
 *   • "Reached" — raw count of respondents who got this far.
 *   • "Fill rate" — % of respondents who reached the element that
 *     actually answered it (great for spotting "people see this
 *     question but skip it" vs. "people drop off here entirely").
 *
 * Extracted out of the inline render block so it can live as a stable
 * sub-section inside the main analytics Card alongside Activity Over
 * Time, instead of in its own standalone Card.
 */
const ElementFunnel = ({ elementAnalytics }) => {
  const BRAND   = '#FE6A6B';   // coral, matches Submissions line above
  const EMERALD = '#10B981';   // matches Conversion rate series above
  const els = elementAnalytics.elements;
  const useReached = els.some((e) => (e.reached || 0) > 0);

  const METRICS = useMemo(() => ([
    {
      key: 'reached',
      label: 'Reached',
      color: BRAND,
      disabled: !useReached,
      // Each row's value vs. the first element (always 100%).
      get baseline() { return Math.max(1, els[0].reached || 0); },
      valueFor: (el) => el.reached || 0,
      format:   (v) => v.toLocaleString(),
    },
    {
      key: 'fill_rate',
      label: 'Fill rate',
      color: EMERALD,
      disabled: false,
      baseline: 100,
      valueFor: (el) => el.fill_rate || 0,
      format:   (v) => `${(v || 0).toFixed(1)}%`,
    },
  ]), [els, useReached]);

  const initial = useReached ? 'reached' : 'fill_rate';
  const [metricKey, setMetricKey] = useState(initial);
  const [hoverId, setHoverId] = useState(null);
  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  const baseline = typeof metric.baseline === 'function' ? metric.baseline() : metric.baseline;

  return (
    <div className="space-y-2" data-testid="element-funnel">
      {/* Toggle chips — mirrors the TrafficLineChart row above. */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="element-funnel-toggles">
        {METRICS.map((m) => {
          const on = metricKey === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => !m.disabled && setMetricKey(m.key)}
              disabled={m.disabled}
              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                on
                  ? 'bg-white text-zinc-900 border-zinc-300'
                  : 'bg-zinc-50 text-zinc-400 border-zinc-200 hover:text-zinc-600'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`element-funnel-toggle-${m.key}`}
              aria-pressed={on}
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: on ? m.color : '#D4D4D8' }}
              />
              {m.label}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-zinc-500 mb-3">
        Visual funnel showing where respondents drop off element-by-element ({elementAnalytics.total_completed} completed + {elementAnalytics.total_abandoned} abandoned).
      </p>

      {els.map((el, idx) => {
        const value = metric.valueFor(el);
        const widthPct = baseline > 0
          ? Math.max(metric.key === 'reached' ? 4 : 0, (value / baseline) * 100)
          : 0;
        const prevValue = idx > 0 ? metric.valueFor(els[idx - 1]) : 0;
        const dropFromPrev = idx > 0 ? Math.max(0, prevValue - value) : 0;
        const dropPctFromPrev = idx > 0 && prevValue > 0
          ? Math.round((dropFromPrev / prevValue) * 100)
          : 0;
        const isHover = hoverId === el.id;
        return (
          <div key={el.id} data-testid={`element-analytics-row-${el.id}`}>
            {idx > 0 && dropPctFromPrev > 0 && (
              <div className="flex items-center justify-center text-[11px] text-zinc-400 -my-0.5 gap-1" data-testid={`element-funnel-drop-${el.id}`}>
                <TrendingDown className="w-3 h-3" />
                -{dropPctFromPrev}% ({metric.format(dropFromPrev)})
              </div>
            )}
            <div
              className="flex items-stretch gap-3 group relative"
              onMouseEnter={() => setHoverId(el.id)}
              onMouseLeave={() => setHoverId(null)}
            >
              {/* Funnel bar */}
              <div className="flex-1 relative h-12 flex items-center justify-center">
                <div
                  className="absolute inset-y-1 rounded-md transition-all"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: metric.color,
                    opacity: Math.max(0.45, widthPct / 100),
                    boxShadow: idx === 0 ? `0 1px 0 ${metric.color}40 inset` : undefined,
                  }}
                />
                <div className="relative z-10 text-xs font-semibold text-white drop-shadow-sm" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.18)' }}>
                  {metric.format(value)}
                </div>
              </div>
              {/* Element metadata sidebar */}
              <div className="w-44 flex flex-col justify-center gap-0.5 py-1">
                <p className="text-sm font-medium text-zinc-900 truncate leading-tight" title={el.question || ''}>
                  <span className="text-zinc-400 mr-1">#{el.order + 1}</span>
                  {el.question || <span className="italic text-zinc-400">(no label)</span>}
                </p>
                <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                  <span className="uppercase tracking-wide">{el.type}</span>
                  {el.required && <span className="text-amber-700">required</span>}
                </div>
              </div>

              {/* Hover tooltip — same visual language as the line
                  chart's tooltip (white card, zinc border, shadow). */}
              {isHover && (
                <div
                  className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-20 px-3 py-2 bg-white border border-zinc-200 rounded-md shadow-sm pointer-events-none text-xs min-w-[200px]"
                  data-testid={`element-funnel-tooltip-${el.id}`}
                >
                  <div className="font-medium text-zinc-900 mb-1.5 truncate">
                    #{el.order + 1} · {el.question || <span className="italic text-zinc-400">(no label)</span>}
                  </div>
                  {useReached && (
                    <div className="flex items-center justify-between gap-3 text-zinc-700">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND }} />
                        Reached
                      </span>
                      <span className="font-medium tabular-nums">{(el.reached || 0).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 text-zinc-700">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: EMERALD }} />
                      Fill rate
                    </span>
                    <span className="font-medium tabular-nums">{(el.fill_rate || 0).toFixed(1)}%</span>
                  </div>
                  {idx > 0 && dropPctFromPrev > 0 && (
                    <div className="flex items-center justify-between gap-3 text-zinc-500 mt-1 pt-1 border-t border-zinc-100">
                      <span>Drop from previous</span>
                      <span className="font-medium tabular-nums">-{dropPctFromPrev}%</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const { formatDate: formatDateTz } = useUserTimezone();
  const { isPaid } = useAuth();
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [formAnalytics, setFormAnalytics] = useState(null);
  const [period, setPeriod] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [blockOwn, setBlockOwn] = useState(() => localStorage.getItem(BLOCK_OWN_KEY) === 'true');
  const [myIp, setMyIp] = useState(null);
  const [elementAnalytics, setElementAnalytics] = useState(null);
  const [elementAnalyticsLoading, setElementAnalyticsLoading] = useState(false);

  useEffect(() => {
    analyticsApi.getMyIp().then(d => setMyIp(d.ip)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchForms();
  }, [blockOwn, myIp]);

  useEffect(() => {
    if (selectedFormId) {
      fetchFormAnalytics(selectedFormId);
    }
  }, [selectedFormId, period, customStart, customEnd, blockOwn, myIp]);

  const excludeIp = blockOwn && myIp ? myIp : null;

  const handleBlockToggle = (checked) => {
    setBlockOwn(checked);
    localStorage.setItem(BLOCK_OWN_KEY, String(checked));
  };

  const fetchDashboard = async () => {
    try {
      const ip = blockOwn && myIp ? myIp : null;
      const data = await analyticsApi.getDashboard(ip);
      setDashboard(data);
    } catch (error) {
      setDashboard({
        summary: { total_forms: 0, published_forms: 0, total_views: 0, total_submissions: 0, avg_completion_rate: 0 },
        chart_data: [],
        top_forms: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      const data = await formsApi.getAll();
      const formsArray = Array.isArray(data) ? data : (data.forms || []);
      setForms(formsArray);
      if (formsArray.length > 0 && !selectedFormId) {
        setSelectedFormId(formsArray[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch forms', error);
      setForms([]);
    }
  };

  const fetchFormAnalytics = async (formId) => {
    try {
      const ip = blockOwn && myIp ? myIp : null;
      const isCustom = period === 'custom';
      const data = await analyticsApi.getFormAnalytics(
        formId,
        ip,
        isCustom ? undefined : parseInt(period),
        isCustom ? customStart : undefined,
        isCustom ? customEnd : undefined
      );
      setFormAnalytics(data);
    } catch (error) {
      setFormAnalytics(null);
    }
    // Also load per-element analytics (fill rate, drop-off, A/B variants).
    setElementAnalyticsLoading(true);
    try {
      const data = await analyticsApi.getFormElementAnalytics(formId);
      setElementAnalytics(data);
    } catch (e) {
      setElementAnalytics(null);
    } finally {
      setElementAnalyticsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <FFLoader size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2 hover:bg-zinc-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-xl font-bold text-zinc-900">Analytics</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!isPaid) {
                    toast.error('Heatmap is a Pro feature. Upgrade to unlock.');
                    return;
                  }
                  if (!selectedFormId) {
                    toast.error('Pick a form first to view its heatmap.');
                    return;
                  }
                  setShowHeatmap(true);
                }}
                className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors group"
                data-testid="open-heatmap-btn"
                title={isPaid ? 'View field-engagement heatmap' : 'Heatmap is a Pro feature'}
              >
                <Flame className="w-4 h-4" />
                <span className="hidden sm:inline">Heatmap</span>
                {!isPaid && (
                  <span className="text-[10px] bg-[#FF6B6B] text-white px-1.5 py-0.5 rounded font-semibold">PRO</span>
                )}
              </button>
              <span className="hidden sm:inline text-zinc-200">|</span>
              <label htmlFor="block-own" className="flex items-center gap-2 text-sm text-zinc-500 cursor-pointer select-none" data-testid="block-own-label">
                <ShieldOff className="w-4 h-4" />
                <span className="hidden sm:inline">Block my own activity</span>
              </label>
              <Switch
                id="block-own"
                checked={blockOwn}
                onCheckedChange={handleBlockToggle}
                data-testid="block-own-toggle"
              />
              <span className="hidden sm:inline text-zinc-200">|</span>
              <button
                type="button"
                onClick={() => {
                  if (!selectedFormId) {
                    toast.error('Pick a form first.');
                    return;
                  }
                  setResetDialogOpen(true);
                }}
                className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-red-600 transition-colors group"
                data-testid="reset-stats-btn"
                title="Reset all stats for this form"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="hidden sm:inline">Reset Stats</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form-specific Analytics — Form Analytics card spans full width;
            traffic sources, breakdowns, per-element funnel and recent
            submissions stack beneath it. */}
        <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Form Analytics</CardTitle>
                <div className="flex items-center gap-3">
                  <Select value={selectedFormId || ''} onValueChange={setSelectedFormId}>
                    <SelectTrigger className="w-[260px]">
                      <SelectValue placeholder="Select a form" />
                    </SelectTrigger>
                    <SelectContent>
                      {forms.map(form => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={period} onValueChange={(val) => {
                    setPeriod(val);
                    if (val !== 'custom') { setCustomStart(''); setCustomEnd(''); }
                  }}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Last 24 hours</SelectItem>
                      <SelectItem value="3">Last 3 days</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last 12 months</SelectItem>
                      <SelectItem value="0">All time</SelectItem>
                    </SelectContent>
                  </Select>
                  {period === 'custom' && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {customStart && customEnd ? `${customStart} – ${customEnd}` : 'Pick dates'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-4 space-y-3" align="end">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-500">Start</label>
                          <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="text-sm" data-testid="custom-start-date" />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-zinc-500">End</label>
                          <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="text-sm" data-testid="custom-end-date" />
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {formAnalytics ? (
                  <div className="space-y-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-zinc-50 rounded-lg p-4">
                        <p className="text-xs text-zinc-500 uppercase">Views</p>
                        <p className="text-xl font-bold">{formAnalytics.summary.total_views}</p>
                      </div>
                      <div className="bg-zinc-50 rounded-lg p-4">
                        <p className="text-xs text-zinc-500 uppercase">Submissions</p>
                        <p className="text-xl font-bold">{formAnalytics.summary.total_submissions}</p>
                      </div>
                      <div className="bg-zinc-50 rounded-lg p-4">
                        <p className="text-xs text-zinc-500 uppercase">Completion Rate</p>
                        <p className="text-xl font-bold">{formAnalytics.summary.completion_rate}%</p>
                      </div>
                      <div className="bg-zinc-50 rounded-lg p-4">
                        <p className="text-xs text-zinc-500 uppercase">Avg. Time</p>
                        <p className="text-xl font-bold">
                          {formAnalytics.summary.avg_completion_time_seconds 
                            ? `${Math.round(formAnalytics.summary.avg_completion_time_seconds / 60)}m` 
                            : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Activity Over Time — now a dependency-free line
                        chart shared with the Admin → Traffic & Revenue
                        panel (`components/TrafficLineChart.js`). Two
                        series: views vs. submissions — both on the left
                        axis, no right-axis labels. We still bucket long
                        windows (>200 days → months, >60 days → weeks)
                        so the x-axis stays legible. */}
                    <div data-testid="form-activity-chart">
                      {(() => {
                        const raw = formAnalytics.chart_data || [];
                        let bucketed = raw;
                        let granularity = 'day';
                        if (raw.length > 200) {
                          // Month buckets — key by YYYY-MM
                          const map = new Map();
                          for (const d of raw) {
                            const key = (d.date || '').slice(0, 7);
                            if (!key) continue;
                            const entry = map.get(key) || { date: `${key}-01`, views: 0, submissions: 0 };
                            entry.views += d.views || 0;
                            entry.submissions += d.submissions || 0;
                            map.set(key, entry);
                          }
                          bucketed = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
                          granularity = 'month';
                        }
                        // Note: previously also bucketed weekly when >60 days,
                        // but users prefer daily granularity for the 30-/90-day
                        // views. We now only switch to monthly past ~6 months.
                        return (
                          <>
                            <div className="flex items-center justify-between mb-3 pt-2 border-t border-zinc-100">
                              <h4 className="text-sm font-semibold text-zinc-800 uppercase tracking-wide pt-3">
                                Activity Over Time
                                {granularity !== 'day' && (
                                  <span className="ml-2 text-xs font-normal text-zinc-400 normal-case tracking-normal">
                                    (grouped by {granularity})
                                  </span>
                                )}
                              </h4>
                            </div>
                            <TrafficLineChart
                              series={bucketed}
                              height={240}
                              seriesDefs={[
                                { key: 'views',           label: 'Views',           color: '#A1A1AA', axis: 'left',  format: 'number'  },
                                { key: 'submissions',     label: 'Submissions',     color: '#FE6A6B', axis: 'left',  format: 'number'  },
                              ]}
                              defaultVisible={{ views: true, submissions: true }}
                            />
                          </>
                        );
                      })()}
                    </div>

                    {/* Funnel by element — second sub-section of the
                        main analytics card. We deliberately keep it
                        inside the same Card as Activity Over Time so
                        the page reads as ONE analytics dashboard with
                        two visualizations (time-series + funnel),
                        sharing the same form picker, period picker,
                        and visual language. */}
                    {selectedFormId && (
                      <div data-testid="element-funnel-section">
                        <div className="flex items-center justify-between mb-3 pt-2 border-t border-zinc-100">
                          <h4 className="text-sm font-semibold text-zinc-800 uppercase tracking-wide pt-3 flex items-center gap-2">
                            <BarChart3 className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                            Funnel by element
                          </h4>
                        </div>
                        {elementAnalyticsLoading && (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                          </div>
                        )}
                        {!elementAnalyticsLoading && elementAnalytics && elementAnalytics.elements.length === 0 && (
                          <p className="text-sm text-zinc-500 py-6 text-center">This form has no elements yet.</p>
                        )}
                        {!elementAnalyticsLoading && elementAnalytics && elementAnalytics.elements.length > 0 && <ElementFunnel elementAnalytics={elementAnalytics} />}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                    <p>Select a form to view analytics</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Traffic Sources */}
            {formAnalytics?.traffic_sources?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Traffic Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {formAnalytics.traffic_sources.map((source, i) => {
                      const total = formAnalytics.traffic_sources.reduce((a, b) => a + b.count, 0);
                      const percentage = (source.count / total * 100).toFixed(1);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">{source.source}</span>
                              <span className="text-sm text-zinc-500">{source.count} ({percentage}%)</span>
                            </div>
                            <Progress value={parseFloat(percentage)} className="h-2" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}


          {/* Data Breakdown Panels — 5 cards in a single row on lg+ so
              Country/City sit next to Browser/Device/OS instead of wrapping
              onto a second row. */}
          {formAnalytics && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <BreakdownPanel title="Browser" icon={Globe} data={formAnalytics.browsers} />
              <BreakdownPanel title="Device" icon={Monitor} data={formAnalytics.devices} />
              <BreakdownPanel title="Operating System" icon={Smartphone} data={formAnalytics.operating_systems} />
              <BreakdownPanel title="Country" icon={MapPin} data={formAnalytics.countries} />
              <BreakdownPanel title="City" icon={Compass} data={formAnalytics.cities} />
            </div>
          )}

          {/* Recent Submissions — full width, sits at the bottom of analytics */}
          {formAnalytics?.recent_submissions?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {formAnalytics.recent_submissions.slice(0, 6).map((sub, i) => (
                    <div key={sub.id || i} className="flex items-center gap-3 p-2 bg-zinc-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FE6A6B20' }}>
                        <Users className="w-4 h-4" style={{ color: '#FE6A6B' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">New submission</p>
                        <p className="text-xs text-zinc-500">
                          {sub.completed_at
                            ? formatDateTz(sub.completed_at)
                            : sub.started_at
                              ? formatDateTz(sub.started_at)
                              : 'Recently'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Heatmap modal — Pro-only field-engagement visualization */}
      {showHeatmap && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          onClick={(e) => { if (e.target === e.currentTarget) setShowHeatmap(false); }}
          data-testid="heatmap-modal-overlay"
        >
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl my-8 p-6" data-testid="heatmap-modal">
            <FormHeatmap
              form={forms.find((f) => f.id === selectedFormId)}
              elementAnalytics={elementAnalytics}
              onClose={() => setShowHeatmap(false)}
            />
          </div>
        </div>
      )}

      {/* Reset Stats confirmation dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={(o) => { if (!resetting) setResetDialogOpen(o); }}>
        <DialogContent data-testid="reset-stats-dialog">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will permanently delete all views, completed submissions, and abandoned data for
              <strong className="text-zinc-900"> {forms.find((f) => f.id === selectedFormId)?.title || 'this form'}</strong>.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setResetDialogOpen(false)}
              disabled={resetting}
              data-testid="reset-stats-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                if (!selectedFormId) return;
                setResetting(true);
                try {
                  await analyticsApi.resetFormStats(selectedFormId);
                  await fetchFormAnalytics(selectedFormId);
                  toast.success('Stats reset.');
                  setResetDialogOpen(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to reset stats.');
                } finally {
                  setResetting(false);
                }
              }}
              disabled={resetting}
              data-testid="reset-stats-confirm"
            >
              {resetting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Yes, Reset Stats
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
